"use client";

import { useAskProperty } from "@/hooks/ai-hooks";
import { AnimatePresence, motion } from "framer-motion";
import { Home, Loader2, Send, Sparkles } from "lucide-react";
import { useLocale } from "next-intl";
import { useEffect, useRef, useState } from "react";

type Msg = { role: "user" | "assistant"; content: string };

export default function AskPropertyChat({
  slug,
  title,
}: {
  slug?: string;
  title?: string;
}) {
  const locale = useLocale();
  const isAr = locale === "ar";
  const ask = useAskProperty();
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [messages, setMessages] = useState<Msg[]>([
    {
      role: "assistant",
      content: isAr
        ? `مرحباً 👋 أنا «${title || "هذا العقار"}». اسألني أي شيء عني — مساحتي، سعري، موقعي، مميزاتي، أو إذا كنت مناسباً لك!`
        : `Hi 👋 I'm "${title || "this property"}". Ask me anything about myself — my area, price, location, features, or whether I'm right for you!`,
    },
  ]);

  const suggestions = isAr
    ? ["هل أنت مناسب لعائلة؟", "كم سعرك ومساحتك؟", "ما مميزاتك؟", "احكِ لي عن موقعك"]
    : ["Are you good for a family?", "Your price & size?", "Your best features?", "Tell me about your location"];

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, ask.isPending]);

  const send = (text: string) => {
    const content = text.trim();
    if (!content || ask.isPending || !slug) return;
    const next = [...messages, { role: "user" as const, content }];
    setMessages(next);
    if (inputRef.current) inputRef.current.value = "";
    ask.mutate(
      { slug, messages: next, locale },
      {
        onSuccess: (d) =>
          setMessages((m) => [...m, { role: "assistant", content: d.message }]),
        onError: () =>
          setMessages((m) => [
            ...m,
            {
              role: "assistant",
              content: isAr
                ? "عذراً، حدث خطأ. حاول مرة أخرى."
                : "Sorry, something went wrong. Please try again.",
            },
          ]),
      },
    );
  };

  if (!slug) return null;

  return (
    <div
      dir={isAr ? "rtl" : "ltr"}
      className="my-10 rounded-[28px] overflow-hidden border border-slate-100 shadow-[0_18px_50px_-20px_rgba(13,53,137,0.25)] bg-white"
    >
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#0a2a6e] via-[#0d3589] to-[#071f52] px-6 py-5">
        <div className="pointer-events-none absolute -top-12 -right-8 size-48 rounded-full bg-amber-400/15 blur-3xl" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-300/70 to-transparent" />
        <div className="relative flex items-center gap-3">
          <div className="size-12 rounded-2xl bg-gradient-to-br from-amber-300 to-amber-500 flex items-center justify-center text-[#0d3589] shadow-lg shadow-amber-500/30">
            <Home size={22} />
          </div>
          <div>
            <h3 className="font-black text-white text-lg flex items-center gap-1.5">
              {isAr ? "تحدّث مع هذا العقار" : "Talk to this property"}
              <Sparkles size={15} className="text-amber-300" />
            </h3>
            <p className="text-xs text-blue-100/70">
              {isAr ? "اسأله مباشرةً — يرد عنك من بياناته الحقيقية" : "Ask it directly — it answers from its real data"}
            </p>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="max-h-[380px] overflow-y-auto px-5 py-5 space-y-3 bg-slate-50/50">
        {messages.map((m, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[82%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === "user"
                  ? "bg-gradient-to-br from-[#0d3589] to-[#071f52] text-white rounded-tr-sm shadow-md"
                  : "bg-white text-slate-700 border border-slate-100 rounded-tl-sm shadow-sm"
              }`}
            >
              {m.content}
            </div>
          </motion.div>
        ))}

        {ask.isPending && (
          <div className="flex justify-start">
            <div className="bg-white border border-slate-100 rounded-2xl px-4 py-3 shadow-sm flex items-center gap-1.5">
              {[0, 150, 300].map((d) => (
                <span key={d} className="size-2 rounded-full bg-amber-400 animate-bounce" style={{ animationDelay: `${d}ms` }} />
              ))}
            </div>
          </div>
        )}

        {messages.length === 1 && !ask.isPending && (
          <div className="flex flex-wrap gap-2 pt-1">
            {suggestions.map((s) => (
              <button
                key={s}
                onClick={() => send(s)}
                className="px-3 py-1.5 text-xs font-medium bg-white border border-slate-200 hover:border-[#0d3589] hover:bg-[#0d3589]/5 rounded-full text-slate-600 hover:text-[#0d3589] transition-all"
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Input */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          send(inputRef.current?.value ?? "");
        }}
        className="flex items-center gap-2 p-3 border-t border-slate-100 bg-white"
      >
        <input
          ref={inputRef}
          placeholder={isAr ? "اكتب سؤالك للعقار..." : "Ask the property..."}
          disabled={ask.isPending}
          className="flex-1 bg-slate-50 border border-slate-100 rounded-2xl px-4 py-3 text-sm text-slate-800 font-medium placeholder:text-slate-400 outline-none focus:border-[#0d3589] focus:bg-white transition-all disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={ask.isPending}
          className="size-11 shrink-0 rounded-2xl bg-gradient-to-br from-[#0d3589] to-[#071f52] text-white flex items-center justify-center hover:shadow-lg active:scale-95 transition-all disabled:opacity-40"
        >
          {ask.isPending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} className={isAr ? "rotate-180" : ""} />}
        </button>
      </form>
    </div>
  );
}
